import { maxBy, throttle } from 'lodash';
import { NextPage } from 'next';
import Link from 'next/link';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'utils/dayjs';
import { filteredVenueDiscounts } from 'utils/filters';
import { useTranslation } from 'utils/i18n';
import { serverSideTranslations } from '../../../lib/serverSideTranslations';

// components

import BookingTimes from 'components/bookingTimes';
import Discount from 'components/discount';
import DiscountTermsModal from 'components/discountTermsModal';
import FloorplansButton from 'components/floorplansButton';
import NearbyVenues from 'components/nearbyVenues';
import Page from 'components/page';
import QuickBook from 'components/quickBook/index';
import SlideShow from 'components/shared/slideShow';
import StickyPanel from 'components/stickyPanel';
import VenueContentBlock from 'components/venueContentBlock';
import VenueContentBlockItem from 'components/venueContentBlockItem';
import VenueHeader from 'components/venueHeader';
import VenueSpaces from 'components/venueSpaces';
import useDataLayer from 'hooks/useDataLayer';
import useNearbyVenues from 'hooks/useNearbyVenues';
import usePropertyDiscount, {
  Discount as DiscountType,
} from 'hooks/usePropertyDiscount';
// hooks
import useStoredBooking from 'hooks/useStoredBooking';
import useVenue, { BookingSlot, Venue } from 'hooks/useVenue';

import { useRouter } from 'next/router';
import { SearchContext } from 'state/search';

const PropertyPage: NextPage = () => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { id } = router.query;

  const venueId = Number.isNaN(Number(id))
    ? Number((id as string).split('-')['0'])
    : Number(id);

  const { data: venue = {} as Venue, loading } = useVenue(venueId, true);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_displayStickyNav, toggleStickyPanel] = useState(true);

  const {
    filters: { date: searchDate },
  } = useContext(SearchContext);

  useEffect(() => {
    const { view, ...query } = router.query;
    if (view && venue)
      router.replace({ pathname: router.pathname, query }, undefined, {
        scroll: false,
        shallow: true,
      });
  }, [router, venue]);

  const [discountTermsModalIsOpen, setDiscountTermsModalIsOpen] =
    useState(false);

  const quickBookRef = useRef<HTMLDivElement | null>(null);

  const [
    { spaceIds: storedSpaceIds, venueId: storedVenueId },
    setStoredBooking,
  ] = useStoredBooking();

  const selectSpace = (space: { id: number }, venue: { id?: number }) => {
    if (storedSpaceIds.length === 0 || storedVenueId === venue.id) {
      setStoredBooking(storedBooking => ({
        ...storedBooking,
        venueId: venue.id,
        spaceIds: [...storedSpaceIds, space.id],
        // The search filter stores the date as a string; normalise to the
        // Date the stored booking declares.
        date: searchDate
          ? moment(searchDate).startOf('day').toDate()
          : storedBooking.date || new Date(),
      }));
    }

    quickBookRef.current?.scrollIntoView({ behavior: 'smooth' });
    // adding as .scrollIntoView above doesn't scroll enough due to sticky header
    window.scrollBy(0, -120);
  };

  const venueDescription = () => {
    return venue.description
      ?.replace(/\n/g, '<br />')
      .replace(/(<? *script)/gi, 'illegalscript');
  };

  const spaceListRef = useRef<HTMLDivElement>(null);
  const hasAnchor = useMemo(
    () =>
      !router.asPath && loading ? false : router.asPath.split('#').length > 1,
    [router, loading]
  );

  useEffect(() => {
    const handleScroll = () => {
      if (spaceListRef.current) {
        const bounds = spaceListRef.current.getBoundingClientRect();

        if (bounds.bottom > window.innerHeight) {
          toggleStickyPanel(true);
        } else {
          toggleStickyPanel(false);
        }
      }
    };

    const throttledHandleScroll = throttle(handleScroll, 500);

    window.addEventListener('scroll', throttledHandleScroll);

    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, []);

  useDataLayer(
    venue?.id
      ? {
          event: 'ospace.property',
          location: venue.address ? venue.address.city : null,
          venueId: venue.id,
          venueName: venue.name,
        }
      : null
  );

  const {
    images: { regular: images = [] } = {},
    facets = [],
    clinicalRooms = [],
    nonClinicalRooms = [],
    organisation = { name: '', largeIcon: '', url: '' },
  } = { ...(loading ? {} : venue) };

  const { discounts = [] } = usePropertyDiscount({
    venueIds: [venueId],
  });

  const venueDiscounts = filteredVenueDiscounts(discounts, venue);

  const { data: venues = [] } = useNearbyVenues({
    venueId,
  });

  if (!venue) {
    return null;
  }

  return (
    <Page
      title={t('headers.property_page.title', {
        name: venue.name,
      })}
      description={venue.description ?? undefined}
    >
      <main className="venues">
        <section>
          <div className="section-content venue-content" ref={spaceListRef}>
            <VenueHeader
              name={venue.name}
              fullAddress={venue.fullAddress}
              organisation={organisation}
            />

            <div className="layout layout--two-col-stacked-left venues">
              <div className="layout__first">
                <div className="venues__slideshow" id="slideShow">
                  {venueDiscounts.length > 0 && (
                    <>
                      <Discount
                        text={t('discounts.save_up_to', {
                          percent: maxBy(
                            venueDiscounts as DiscountType[],
                            discount => discount.amount
                          )?.amount,
                        })}
                        rotated
                        size="large"
                        onClick={() => setDiscountTermsModalIsOpen(true)}
                      />

                      <DiscountTermsModal
                        isOpen={discountTermsModalIsOpen}
                        onClose={() => setDiscountTermsModalIsOpen(false)}
                        propertyOrRoomName={venue.name}
                        discounts={venueDiscounts}
                      />
                    </>
                  )}

                  {venue.address && (
                    <SlideShow slideUrls={images} mapLatLng={venue.latLng} />
                  )}

                  <FloorplansButton venue={venue} />
                </div>
                <div className="venues__summary">
                  {/* eslint-disable react/no-danger */}
                  <div
                    dangerouslySetInnerHTML={{
                      __html: venueDescription() ?? '',
                    }}
                  />
                  {/* eslint-enable react/no-danger */}
                </div>
                <BookingTimes
                  bookingSlots={venue.bookingSlots as unknown as BookingSlot[]}
                  openingTimes={venue.nonClinicalOpeningTimes}
                  clinicalRooms={clinicalRooms.length > 0}
                  nonClinicalRooms={nonClinicalRooms.length > 0}
                  text={t('venues.booking_times_text')}
                />
                <StickyPanel show={false}>
                  <Link
                    href={`/properties/${venue.id}#venue-rooms-section`}
                    className="button button--primary button--block"
                  >
                    {t('venues.book_rooms')}
                  </Link>
                </StickyPanel>
              </div>
              <div className="layout__last">
                <div className="venues__book-now-button">
                  <Link
                    href={`/properties/${venue.id}#venue-rooms-section`}
                    className="button button--primary button--block"
                  >
                    {t('venues.book_rooms')}
                  </Link>
                </div>
                {facets.length > 0 && (
                  <ul className="venue-content-block__block">
                    <VenueContentBlock title={t('shared.facilities')}>
                      {facets.map(facet => (
                        <VenueContentBlockItem
                          key={facet.name}
                          count={undefined}
                          title={facet.name}
                          icon={facet.icon}
                          iconAltText={facet.iconAltText}
                          displayCount={false}
                        />
                      ))}
                    </VenueContentBlock>
                  </ul>
                )}
                {clinicalRooms.length > 0 && (
                  <ul className="venue-content-block__block">
                    <VenueContentBlock title={t('venues.clinical_rooms')}>
                      {clinicalRooms.map(room => (
                        <VenueContentBlockItem
                          key={room.name}
                          count={room.count}
                          title={room.name}
                          icon={undefined}
                          iconAltText={undefined}
                        />
                      ))}
                    </VenueContentBlock>
                  </ul>
                )}
                {nonClinicalRooms.length > 0 && (
                  <ul className="venue-content-block__block">
                    <VenueContentBlock title={t('venues.non_clinical_rooms')}>
                      {nonClinicalRooms.map(room => (
                        <VenueContentBlockItem
                          key={room.name}
                          count={room.count}
                          title={room.name}
                          icon={undefined}
                          iconAltText={undefined}
                        />
                      ))}
                    </VenueContentBlock>
                  </ul>
                )}
                <ul className="venue-content-block__block">
                  <VenueContentBlock
                    title={t('venues.property_contact_details')}
                  >
                    <ul className="contact-details__list">
                      <li className="contact-details__wrap">
                        <div className="contact-details__icon">
                          <img
                            src="/images/icons/rooms-properties/phone.svg"
                            alt={t('venues.contact_methods.telephone')}
                          />
                        </div>
                        <a href={`tel:${venue.telephone}`}>{venue.telephone}</a>
                      </li>
                      <li className="contact-details__wrap contact-details__email">
                        <div className="contact-details__icon">
                          <img
                            src="/images/icons/rooms-properties/email.svg"
                            alt={t('venues.contact_methods.email')}
                          />
                        </div>
                        <a href={`mailto:${venue.contactEmail}`}>
                          {venue.contactEmail}
                        </a>
                      </li>
                      <li className="contact-details__wrap">
                        <div className="contact-details__icon">
                          <img
                            src="/images/icons/rooms-properties/help-faq.svg"
                            alt={t('property.contact_details.help_and_faq')}
                          />
                        </div>
                        <Link href={t('navigation.help.url')}>
                          {t('property.contact_details.help_and_faq')}
                        </Link>
                      </li>
                    </ul>
                  </VenueContentBlock>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {venue && venues.length > 0 && venue.address && (
          <NearbyVenues location={venue.address.postcode} venues={venues} />
        )}

        <section
          className="highlighted"
          id="venue-rooms-section"
          data-cy="venueRoomsList"
        >
          <div className="section-content">
            <div className="layout layout--two-col-stacked-left">
              <div className="layout__first">
                {typeof window !== 'undefined' ? (
                  <VenueSpaces
                    venue={venue}
                    selectedVenueId={storedVenueId}
                    selectedSpaceIds={storedSpaceIds}
                    onAddToBooking={selectSpace}
                    scrollIntoView={!loading && hasAnchor}
                  />
                ) : null}
              </div>
              <div className="layout__last">
                <QuickBook ref={quickBookRef} />
              </div>
            </div>
          </div>
        </section>
      </main>
    </Page>
  );
};

export const getServerSideProps = async ({ locale }: { locale: string }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
};

export default PropertyPage;
